import React, { FC, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Text, Button, Flex, TextField } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";

export const BillingRootPage: FC = () => {
  const navigate = useNavigate();
  const [spaceAccountId, setSpaceAccountId] = useState<string>("");

  const { data: spacesData } = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });

  const { data: spaceAccountsData } = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  interface MinimalSpace {
    id: string;
    is_primary_space?: boolean;
  }

  interface MinimalSpaceAccount {
    id: string;
    space_id: string;
  }

  const primarySpaceAccountId = useMemo(() => {
    const spaces = (spacesData?.data as MinimalSpace[] | undefined) ?? undefined;
    const accounts = (spaceAccountsData?.data as MinimalSpaceAccount[] | undefined) ?? undefined;
    if (!spaces || !accounts) return null;

    const primarySpace = spaces.find(space => space.is_primary_space);
    if (!primarySpace) return null;

    const primarySpaceAccount = accounts.find(account => account.space_id === primarySpace.id);
    return primarySpaceAccount?.id ?? null;
  }, [spacesData, spaceAccountsData]);

  const handleUseMyAccount = () => {
    if (primarySpaceAccountId) setSpaceAccountId(primarySpaceAccountId);
  };

  const handleGo = () => {
    if (!spaceAccountId) return;
    navigate(atlasRoutes.billingView({ params: { spaceAccountId: spaceAccountId.trim() } }).path);
  };
  return (
    <Container>
      <Flex justify="between" align="center" mb="4">
        <div>
          <h1>Billing</h1>
          <DescriptionText>Enter a Space Account ID to view billing summary.</DescriptionText>
        </div>
      </Flex>
      <FormContainer onSubmit={e => e.preventDefault()}>
        <TextField.Root
          placeholder="Space Account ID..."
          value={spaceAccountId}
          onChange={e => setSpaceAccountId(e.currentTarget.value)}
          maxLength={100}
          aria-label="Space Account ID"
          style={{ flex: 1 }}
        />
        <Button type="button" onClick={handleGo} disabled={!spaceAccountId}>
          View Billing
        </Button>
        <Button
          type="button"
          variant="soft"
          onClick={handleUseMyAccount}
          disabled={!primarySpaceAccountId}
        >
          Use my Space Account
        </Button>
      </FormContainer>
    </Container>
  );
};
const Container = styled.div`
  width: 100%;
`;
const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const FormContainer = styled.form`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  max-width: 720px;
`;
